import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaBookOpen,
  FaChevronDown,
  FaCommentDots,
  FaEnvelope,
  FaExclamationCircle,
  FaPhoneAlt,
  FaPlus,
  FaQuestionCircle,
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES, adminPath } from '../constants/adminPortal';
import '../styles/AdminHelpSupport.css';
import { graphqlRequest } from '../utils/graphqlRequest';
import { SUPPORT_FAQS_PUBLISHED_QUERY } from '../graphql/supportFaq';
import { SUPPORT_MY_TICKETS_QUERY } from '../graphql/supportTicket';
import { REPORTED_MY_ISSUES_QUERY } from '../graphql/reportedIssue';

const QUICK_HELP = [
  { id: 'faq', label: 'FAQs', icon: FaQuestionCircle },
  { id: 'report', label: 'Report Issue', icon: FaExclamationCircle },
  { id: 'new-ticket', label: 'New Ticket', icon: FaPlus },
  { id: 'live-chat', label: 'Live Chat', icon: FaCommentDots },
];

const FAQS = [
  {
    id: 'faq-1',
    question: 'How do I clock in/out for a shift?',
    category: 'Time Tracking',
    answer:
      'Open your dashboard and use the Clock In button when your shift starts. Use Clock Out when you finish. You can also use Manual Timesheet if you need to add or correct an entry.',
  },
  {
    id: 'faq-2',
    question: 'How to submit a work report?',
    category: 'Reports',
    answer:
      'Open Work Report from the sidebar, select the project or shift, attach photos or notes as required, and submit. You’ll see confirmation when it’s saved.',
  },
  {
    id: 'faq-3',
    question: 'When will I receive my payment?',
    category: 'Payments',
    answer:
      'Payment schedules depend on your employer’s payroll cycle. Check Payslip for issued payments and contact your payroll team for specific dates.',
  },
  {
    id: 'faq-4',
    question: 'How to update my availability?',
    category: 'Calendar',
    answer:
      'Open Calendar from the sidebar and adjust your availability or blocked times. Changes may apply immediately or after your next sync, depending on your organisation settings.',
  },
  {
    id: 'faq-5',
    question: 'How to accept or decline a job?',
    category: 'Jobs',
    answer:
      'Go to My Job or your assigned tasks list, open the job card, and choose the available action (Accept / Decline) before the deadline shown.',
  },
];

const statusClass = (value) => {
  const normalized = String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '-');
  if (normalized.includes('resolved')) return 'resolved';
  if (normalized.includes('progress')) return 'progress';
  return 'open';
};

function ticketIdLabel(t) {
  const n = Number(t?.ticketNumber);
  if (!Number.isFinite(n) || n <= 0) return String(t?.id || '—');
  return `TKT-${String(n).padStart(3, '0')}`;
}

function statusLabel(value) {
  const v = String(value || '').toUpperCase();
  if (v === 'IN_PROGRESS') return 'In Progress';
  if (v === 'RESOLVED') return 'Resolved';
  return 'Open';
}

function formatTicketDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
  } catch {
    return '—';
  }
}

function issueIdLabel(row) {
  const n = Number(row?.issueNumber);
  if (!Number.isFinite(n) || n <= 0) return String(row?.id || '—');
  return `ISS-${String(n).padStart(3, '0')}`;
}

const HelpSupport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openFaqId, setOpenFaqId] = useState(null);
  const [savedFaqs, setSavedFaqs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState('');

  const [issues, setIssues] = useState([]);
  const [issuesLoading, setIssuesLoading] = useState(true);
  const [issuesError, setIssuesError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await graphqlRequest(SUPPORT_FAQS_PUBLISHED_QUERY);
        const rows = data?.supportFaqsPublished || [];
        if (!cancelled) setSavedFaqs(rows);
      } catch {
        if (!cancelled) setSavedFaqs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTicketsLoading(true);
      setTicketsError('');
      try {
        const data = await graphqlRequest(SUPPORT_MY_TICKETS_QUERY);
        const rows = Array.isArray(data?.supportMyTickets) ? data.supportMyTickets : [];
        if (!cancelled) setTickets(rows);
      } catch (e) {
        if (!cancelled) {
          setTicketsError(e?.message || 'Could not load tickets');
          setTickets([]);
        }
      } finally {
        if (!cancelled) setTicketsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIssuesLoading(true);
      setIssuesError('');
      try {
        const data = await graphqlRequest(REPORTED_MY_ISSUES_QUERY);
        const rows = Array.isArray(data?.reportedMyIssues) ? data.reportedMyIssues : [];
        if (!cancelled) setIssues(rows);
      } catch (e) {
        if (!cancelled) {
          setIssuesError(e?.message || 'Could not load reported issues');
          setIssues([]);
        }
      } finally {
        if (!cancelled) setIssuesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const helpFaqList = useMemo(() => {
    const fromApi = savedFaqs.map((r) => ({
      id: `saved-${r.id}`,
      question: r.question,
      category: r.category,
      answer: r.answer,
    }));
    return [...FAQS, ...fromApi];
  }, [savedFaqs]);

  const ticketCards = useMemo(() => {
    const sorted = [...tickets].sort((a, b) => {
      const da = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
      const db = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
      return db - da;
    });
    return sorted.slice(0, 3).map((t) => ({
      id: t.id,
      displayId: ticketIdLabel(t),
      subject: t.subject || '—',
      status: statusLabel(t.status),
      date: formatTicketDate(t.createdAt),
    }));
  }, [tickets]);

  const issueCards = useMemo(() => {
    const sorted = [...issues].sort((a, b) => {
      const da = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
      const db = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
      return db - da;
    });
    return sorted.slice(0, 3).map((row) => ({
      id: row.id,
      displayId: issueIdLabel(row),
      title: row.title || '—',
      status: statusLabel(row.status),
      date: formatTicketDate(row.createdAt),
    }));
  }, [issues]);

  const supportBanner =
    typeof location.state?.supportBanner === 'string' ? location.state.supportBanner : null;

  const startLiveChat = useCallback(() => {
    window.dispatchEvent(new Event('admin-live-chat-open'));
  }, []);

  const goFaq = useCallback(
    () => navigate(adminPath(ADMIN_ROUTES.FAQ)),
    [navigate],
  );

  const goReportIssue = useCallback(
    () => navigate(adminPath(ADMIN_ROUTES.REPORT_ISSUE)),
    [navigate],
  );

  const goNewTicket = useCallback(
    () => navigate(adminPath(ADMIN_ROUTES.NEW_TICKET)),
    [navigate],
  );

  const quickHelpRows = useMemo(
    () =>
      QUICK_HELP.map((entry) => ({
        ...entry,
        onClick:
          entry.id === 'live-chat'
            ? startLiveChat
            : entry.id === 'faq'
              ? goFaq
              : entry.id === 'report'
                ? goReportIssue
                : entry.id === 'new-ticket'
                  ? goNewTicket
                  : undefined,
      })),
    [goFaq, goNewTicket, goReportIssue, startLiveChat],
  );

  return (
    <section className="help-support-page">
      <div className="help-support-card">
        <header className="help-support-header">
          <button
            type="button"
            className="help-support-back-btn"
            onClick={() => navigate(-1)}
            title="Go back"
          >
            <FaArrowLeft />
          </button>
          <div>
            <h1>Help &amp; Support</h1>
            <p>We&apos;re here to help you</p>
          </div>
        </header>

        <div className="help-support-body">
          {supportBanner ? (
            <div className="help-support-flash" role="status">
              {supportBanner}
            </div>
          ) : null}

          <div className="help-support-layout">
            <div className="help-support-main">
              <section className="help-support-section">
                <div className="help-support-section-head">
                  <h2>Frequently Asked Questions</h2>
                  <button type="button" className="help-support-view-all" onClick={goFaq}>
                    View All
                  </button>
                </div>
                <ul className="help-support-faq-list">
                  {helpFaqList.map((faq) => {
                    const expanded = openFaqId === faq.id;
                    return (
                      <li
                        key={faq.id}
                        className={`help-support-faq-item ${expanded ? 'open' : ''}`}
                      >
                        <button
                          type="button"
                          className="help-support-faq-trigger"
                          id={`help-faq-trigger-${faq.id}`}
                          aria-expanded={expanded}
                          aria-controls={`help-faq-panel-${faq.id}`}
                          onClick={() => setOpenFaqId(expanded ? null : faq.id)}
                        >
                          <div className="help-support-faq-trigger-text">
                            <p className="help-support-faq-question">{faq.question}</p>
                            <small className="help-support-faq-category">{faq.category}</small>
                          </div>
                          <FaChevronDown className="help-support-faq-chevron" aria-hidden />
                        </button>
                        {expanded ? (
                          <div
                            id={`help-faq-panel-${faq.id}`}
                            className="help-support-faq-panel"
                            role="region"
                            aria-labelledby={`help-faq-trigger-${faq.id}`}
                          >
                            <p>{faq.answer}</p>
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </section>

              <section className="help-support-section">
                <div className="help-support-section-head">
                  <h2>Your Tickets</h2>
                  <button type="button" className="help-support-new-ticket-btn" onClick={goNewTicket}>
                    <FaPlus />
                    New Ticket
                  </button>
                </div>
                {ticketsError ? (
                  <p className="help-support-error" role="status">
                    {ticketsError}
                  </p>
                ) : null}
                <div className="help-support-tickets">
                  {ticketsLoading ? (
                    <p className="help-support-empty">Loading…</p>
                  ) : ticketCards.length === 0 ? (
                    <p className="help-support-empty">No tickets yet.</p>
                  ) : null}
                  {ticketCards.map((ticket) => (
                    <article key={ticket.id} className="help-support-ticket-card">
                      <div className="help-support-ticket-top">
                        <div>
                          <strong>{ticket.displayId}</strong>
                          <small>{ticket.date}</small>
                        </div>
                        <span className={`help-support-status ${statusClass(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </div>
                      <p>{ticket.subject}</p>
                      <button
                        type="button"
                        onClick={() => navigate(`/support-management/tickets/${ticket.id}/resolve`)}
                      >
                        View Details
                      </button>
                    </article>
                  ))}
                </div>
              </section>

              <section className="help-support-section">
                <div className="help-support-section-head">
                  <h2>Your Reported Issues</h2>
                  <button
                    type="button"
                    className="help-support-view-all"
                    onClick={() => navigate(adminPath(ADMIN_ROUTES.SUPPORT_MGMT_REPORTED_ISSUES))}
                  >
                    View All
                  </button>
                </div>
                {issuesError ? (
                  <p className="help-support-error" role="status">
                    {issuesError}
                  </p>
                ) : null}
                <div className="help-support-tickets">
                  {issuesLoading ? (
                    <p className="help-support-empty">Loading…</p>
                  ) : issueCards.length === 0 ? (
                    <p className="help-support-empty">No reported issues yet.</p>
                  ) : null}
                  {issueCards.map((row) => (
                    <article key={row.id} className="help-support-ticket-card">
                      <div className="help-support-ticket-top">
                        <div>
                          <strong>{row.displayId}</strong>
                          <small>{row.date}</small>
                        </div>
                        <span className={`help-support-status ${statusClass(row.status)}`}>
                          {row.status}
                        </span>
                      </div>
                      <p>{row.title}</p>
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `${adminPath(ADMIN_ROUTES.SUPPORT_MANAGEMENT)}/reported-issues/${row.id}`,
                          )
                        }
                      >
                        View Details
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            </div>

            <aside className="help-support-aside">
              <section className="help-support-section help-support-section--aside">
                <h2>Quick Help</h2>
                <div className="help-support-quick-grid">
                  {quickHelpRows.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className="help-support-quick-item"
                        onClick={item.onClick}
                      >
                        <span className="help-support-quick-icon">
                          <Icon />
                        </span>
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="help-support-section help-support-section--aside">
                <h2>Contact Support</h2>
                <div className="help-support-contact-rows">
                  <div className="help-support-contact-row">
                    <span>
                      <FaEnvelope />
                    </span>
                    <div>
                      <p>Email Support</p>
                      <small>support@cleaningsapp.com</small>
                    </div>
                  </div>
                  <div className="help-support-contact-row">
                    <span>
                      <FaPhoneAlt />
                    </span>
                    <div>
                      <p>Phone Support</p>
                      <small>+61 800_SUPPORT</small>
                    </div>
                  </div>
                  <div className="help-support-contact-row">
                    <span>
                      <FaBookOpen />
                    </span>
                    <div>
                      <p>Office Hours</p>
                      <small>Mon - Fri, 9:00 AM - 6:00 PM</small>
                    </div>
                  </div>
                </div>
              </section>
            </aside>
          </div>
        </div>

      </div>
    </section>
  );
};

export default HelpSupport;
